import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  FilterSelectOption,
  formatMultiSelectLabel,
  useFilterPanelFloatingMenu,
} from './filterDropdownShared';

export type { FilterSelectOption } from './filterDropdownShared';
export { formatMultiSelectLabel } from './filterDropdownShared';

type FilterMultiSelectProps = {
  id: string;
  options: FilterSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
  disabled?: boolean;
};

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  id,
  options,
  selected,
  onChange,
  allLabel = 'Todos',
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const listboxId = `${id}-listbox`;

  const rowCount = options.length + 1;
  const menuLayout = useFilterPanelFloatingMenu(open, rowCount, { wrapRef, triggerRef, menuRef }, () =>
    setOpen(false),
  );

  const displayLabel = useMemo(
    () => formatMultiSelectLabel(selected, options, allLabel),
    [allLabel, options, selected],
  );

  const handleOptionMouseDown = (event: React.MouseEvent, value: string) => {
    event.preventDefault();
    if (disabled) return;

    if (value === '') {
      onChange([]);
      if (!event.ctrlKey && !event.metaKey) setOpen(false);
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      if (selected.includes(value)) {
        onChange(selected.filter((item) => item !== value));
      } else {
        onChange([...selected, value]);
      }
      return;
    }

    onChange([value]);
    setOpen(false);
  };

  const menu =
    open && menuLayout ? (
      <ul
        ref={menuRef}
        id={listboxId}
        role="listbox"
        className="filter-panel__select-menu filter-panel__select-menu--floating"
        aria-multiselectable="true"
        style={{
          top: menuLayout.top,
          left: menuLayout.left,
          width: menuLayout.width,
          height: menuLayout.height,
        }}
      >
        <li
          role="option"
          aria-selected={selected.length === 0}
          className={`filter-panel__select-option${
            selected.length === 0 ? ' filter-panel__select-option--selected' : ''
          }`}
          onMouseDown={(event) => handleOptionMouseDown(event, '')}
        >
          {allLabel}
        </li>
        {options.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <li
              key={option.value}
              role="option"
              aria-selected={isSelected}
              className={`filter-panel__select-option${isSelected ? ' filter-panel__select-option--selected' : ''}`}
              onMouseDown={(event) => handleOptionMouseDown(event, option.value)}
            >
              {option.label}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div
      className={`filter-panel__select-wrap${open ? ' filter-panel__select-wrap--open' : ''}`}
      ref={wrapRef}
    >
      <button
        type="button"
        id={id}
        ref={triggerRef}
        role="combobox"
        className="filter-panel__control filter-panel__select filter-panel__select-trigger form-select form-select-sm"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="filter-panel__select-trigger-label text-truncate">{displayLabel}</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
};

export default FilterMultiSelect;
